/**
 * Centralized pricing configuration — single source of truth for the admin
 * frontend landing page and pricing sections.
 *
 * Data is aligned with the backend `feature_matrix.py` tier definitions.
 * When updating pricing or entitlements, update this file AND the backend
 * matrix together.
 *
 * @typedef {Object} PricingTier
 * @property {string} id - Tier identifier (starter | growth | pro | enterprise)
 * @property {string} name - Display name
 * @property {number} price - Monthly price in USD (0 = custom / contact sales)
 * @property {number} yearlyPrice - Yearly price per month in USD (0 = custom)
 * @property {string} description - Short marketing description
 * @property {boolean} highlight - Whether to show "Most Popular" badge
 * @property {string} ctaLabel - Call-to-action button text
 * @property {string[]} features - Bullet-point feature list for the tier card
 *
 * @typedef {Object} PricingFeature
 * @property {string} key - Maps to backend feature key
 * @property {string} label - Human-readable feature name
 * @property {string|boolean} starter - Starter tier value
 * @property {string|boolean} growth - Growth tier value
 * @property {string|boolean} pro - Pro tier value
 * @property {string|boolean} enterprise - Enterprise tier value
 * @property {string} category - Feature category name
 *
 * @typedef {Object} FaqItem
 * @property {string} question
 * @property {string} answer
 */

// ---------------------------------------------------------------------------
// Tiers
// ---------------------------------------------------------------------------

/** @type {readonly PricingTier[]} */
export const TIERS = Object.freeze([
  {
    id: 'starter',
    name: 'Starter',
    price: 29,
    yearlyPrice: 24,
    description: 'Everything you need to launch your first print-on-demand products on Etsy.',
    highlight: false,
    ctaLabel: 'Start Free Trial',
    features: [
      '1 Etsy shop connection',
      'No Shopify integration',
      '50 exports / month',
      'Basic mockup generator',
      'Manual gang sheet placement',
      'Drag-and-drop canvas',
      'Email support',
      '1 team seat',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 99,
    yearlyPrice: 79,
    description: 'Scale with Shopify, auto-layout, DPI scoring, and batch processing.',
    highlight: true,
    ctaLabel: 'Start Free Trial',
    features: [
      '1 Etsy + 1 Shopify store',
      '300 exports / month',
      'Auto-layout engine',
      'AI product tagging',
      'DPI scoring & quality analysis',
      'Batch mockup generation',
      'Custom domain storefront',
      'Full CMS builder',
      'Bulk order actions',
      'Basic analytics',
      '1 team seat',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 249,
    yearlyPrice: 199,
    description: 'Advanced tools for high-volume print operations and teams.',
    highlight: false,
    ctaLabel: 'Start Free Trial',
    features: [
      'Up to 5 Etsy + 5 Shopify stores',
      '1,000 exports / month',
      'Advanced layout optimization',
      'White-label branding',
      'Multi-user with roles (5 seats)',
      'Advanced analytics',
      'Priority processing queue',
      'Scheduled production runs',
      'Priority support',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 0,
    yearlyPrice: 0,
    description: 'Custom solutions for high-volume sellers and print shops with dedicated support.',
    highlight: false,
    ctaLabel: 'Contact Sales',
    features: [
      'Everything in Pro',
      'Unlimited stores & exports',
      'Unlimited team seats',
      'Dedicated account manager',
      'Custom API integrations',
      'Custom gang sheet sizes',
      'SLA & uptime guarantee',
      'Onboarding & training',
    ],
  },
]);

// ---------------------------------------------------------------------------
// Feature comparison categories
// ---------------------------------------------------------------------------

/** @type {readonly string[]} */
export const FEATURE_CATEGORIES = Object.freeze([
  'Sales Channels',
  'Mockups & AI',
  'Gang Sheet Builder',
  'Exports & Production',
  'Storefront & Commerce',
  'Analytics & Automation',
  'Team & White-label',
]);

// ---------------------------------------------------------------------------
// Feature comparison matrix
// ---------------------------------------------------------------------------

/** @type {readonly PricingFeature[]} */
export const FEATURES = Object.freeze([
  // Sales Channels
  {
    key: 'etsy_shops',
    label: 'Etsy shop connections',
    starter: '1',
    growth: '1',
    pro: 'Up to 5',
    enterprise: 'Unlimited',
    category: 'Sales Channels',
  },
  {
    key: 'shopify_integration',
    label: 'Shopify integration',
    starter: false,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Sales Channels',
  },
  {
    key: 'shopify_stores',
    label: 'Shopify store connections',
    starter: false,
    growth: '1',
    pro: 'Up to 5',
    enterprise: 'Unlimited',
    category: 'Sales Channels',
  },
  {
    key: 'bulk_listing',
    label: 'Bulk listing tools',
    starter: false,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Sales Channels',
  },
  {
    key: 'listing_sync',
    label: 'Automatic listing sync',
    starter: true,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Sales Channels',
  },

  // Mockups & AI
  {
    key: 'mockup_generator',
    label: 'Mockup generator',
    starter: 'Basic',
    growth: 'Batch',
    pro: 'Batch',
    enterprise: 'Batch',
    category: 'Mockups & AI',
  },
  {
    key: 'ai_tagging',
    label: 'AI product tagging',
    starter: false,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Mockups & AI',
  },
  {
    key: 'dpi_scoring',
    label: 'DPI scoring & quality analysis',
    starter: false,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Mockups & AI',
  },
  {
    key: 'custom_mockup_templates',
    label: 'Custom mockup templates',
    starter: false,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Mockups & AI',
  },
  {
    key: 'ai_description',
    label: 'AI listing descriptions',
    starter: false,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Mockups & AI',
  },

  // Gang Sheet Builder
  {
    key: 'gang_sheet_sizes',
    label: 'Gang sheet sizes',
    starter: '1 size',
    growth: 'All sizes',
    pro: 'All sizes',
    enterprise: 'All + custom',
    category: 'Gang Sheet Builder',
  },
  {
    key: 'auto_layout',
    label: 'Auto-layout optimization',
    starter: false,
    growth: true,
    pro: 'Advanced',
    enterprise: 'Advanced',
    category: 'Gang Sheet Builder',
  },
  {
    key: 'drag_drop_canvas',
    label: 'Drag-and-drop canvas',
    starter: true,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Gang Sheet Builder',
  },

  // Exports & Production
  {
    key: 'exports_per_month',
    label: 'Exports / month',
    starter: '50',
    growth: '300',
    pro: '1,000',
    enterprise: 'Unlimited',
    category: 'Exports & Production',
  },
  {
    key: 'print_methods',
    label: 'Print methods (DTF, UVDTF, sublimation)',
    starter: true,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Exports & Production',
  },
  {
    key: 'priority_queue',
    label: 'Priority processing queue',
    starter: false,
    growth: false,
    pro: true,
    enterprise: true,
    category: 'Exports & Production',
  },
  {
    key: 'scheduled_runs',
    label: 'Scheduled production runs',
    starter: false,
    growth: false,
    pro: true,
    enterprise: true,
    category: 'Exports & Production',
  },
  {
    key: 'order_routing',
    label: 'Order routing & automation',
    starter: false,
    growth: false,
    pro: true,
    enterprise: true,
    category: 'Exports & Production',
  },
  {
    key: 'packing_slips',
    label: 'Custom packing slips',
    starter: false,
    growth: false,
    pro: true,
    enterprise: true,
    category: 'Exports & Production',
  },
  {
    key: 'shipping_labels',
    label: 'Shipping label generation',
    starter: false,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Exports & Production',
  },

  // Storefront & Commerce
  {
    key: 'craftflow_storefront',
    label: 'CraftFlow Commerce storefront',
    starter: false,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Storefront & Commerce',
  },
  {
    key: 'custom_domain',
    label: 'Custom domain',
    starter: false,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Storefront & Commerce',
  },
  {
    key: 'cms_full',
    label: 'Full CMS page builder',
    starter: false,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Storefront & Commerce',
  },
  {
    key: 'stripe_checkout',
    label: 'Stripe checkout',
    starter: false,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Storefront & Commerce',
  },
  {
    key: 'customer_accounts',
    label: 'Customer accounts & order history',
    starter: false,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Storefront & Commerce',
  },

  // Analytics & Automation
  {
    key: 'sales_analytics',
    label: 'Sales analytics dashboard',
    starter: false,
    growth: 'Basic',
    pro: 'Advanced',
    enterprise: 'Advanced',
    category: 'Analytics & Automation',
  },
  {
    key: 'customer_analytics',
    label: 'Customer analytics',
    starter: false,
    growth: false,
    pro: true,
    enterprise: true,
    category: 'Analytics & Automation',
  },
  {
    key: 'email_marketing',
    label: 'Email marketing (SendGrid)',
    starter: false,
    growth: false,
    pro: true,
    enterprise: true,
    category: 'Analytics & Automation',
  },
  {
    key: 'inventory_alerts',
    label: 'Inventory alerts',
    starter: false,
    growth: true,
    pro: true,
    enterprise: true,
    category: 'Analytics & Automation',
  },

  // Team & White-label
  {
    key: 'team_seats',
    label: 'Team seats',
    starter: '1',
    growth: '1',
    pro: '5',
    enterprise: 'Unlimited',
    category: 'Team & White-label',
  },
  {
    key: 'multi_user',
    label: 'Multi-user access',
    starter: false,
    growth: false,
    pro: true,
    enterprise: true,
    category: 'Team & White-label',
  },
  {
    key: 'role_management',
    label: 'Role-based permissions',
    starter: false,
    growth: false,
    pro: true,
    enterprise: true,
    category: 'Team & White-label',
  },
  {
    key: 'white_label',
    label: 'White-label storefront',
    starter: false,
    growth: false,
    pro: true,
    enterprise: true,
    category: 'Team & White-label',
  },
  {
    key: 'api_access',
    label: 'Custom API integrations',
    starter: false,
    growth: false,
    pro: false,
    enterprise: true,
    category: 'Team & White-label',
  },
]);

// ---------------------------------------------------------------------------
// FAQ items
// ---------------------------------------------------------------------------

/** @type {readonly FaqItem[]} */
export const FAQ_ITEMS = Object.freeze([
  {
    question: 'Can I switch plans at any time?',
    answer:
      'Yes, you can upgrade or downgrade your plan at any time. When you upgrade, you get immediate access to the new features and we prorate the cost. When you downgrade, the change takes effect at the end of your current billing cycle.',
  },
  {
    question: 'What counts as a gang sheet export?',
    answer:
      'Each time you export a completed gang sheet layout as a print-ready file (PNG or PDF), that counts as one export. Previewing your layout, rearranging designs, or saving drafts does not count toward your export limit.',
  },
  {
    question: 'Do I need a Growth plan to sell on Shopify?',
    answer:
      'Yes, the Shopify integration, CraftFlow Commerce storefront, and custom domain are available on the Growth plan and above. The Starter plan supports Etsy shop connections only.',
  },
  {
    question: 'How does AI product tagging work?',
    answer:
      'Our AI analyzes your product images using computer vision to automatically generate relevant tags, titles, and descriptions optimized for Etsy and Shopify search. This saves hours of manual listing work and helps improve your search visibility.',
  },
  {
    question: 'Is there a free trial?',
    answer:
      'Every paid plan comes with a 14-day free trial, no credit card required. You get full access to all the features in your chosen plan during the trial period. If you decide not to continue, your account simply reverts to our free tier.',
  },
  {
    question: 'What print methods are supported?',
    answer:
      'CraftFlow supports DTF (Direct to Film), UVDTF, sublimation, and vinyl print methods. The gang sheet builder is optimized for DTF and UVDTF workflows, and you can configure custom export settings for each method.',
  },
  {
    question: 'Can I connect multiple Etsy shops?',
    answer:
      'The Starter and Growth plans each support 1 Etsy shop. The Pro plan supports up to 5, and Enterprise supports unlimited shop connections. Each shop syncs independently with its own listings, orders, and analytics.',
  },
  {
    question: 'What happens to my data if I cancel?',
    answer:
      'Your data is retained for 30 days after cancellation. During that period you can reactivate your account and pick up right where you left off. After 30 days, data is permanently deleted in accordance with our privacy policy.',
  },
]);
